/**
 * Singleton class that holds the Network Plan cache for Arqiva networks
 */
import axios from 'axios'
import auth from "./auth"
import UserSessionService from "./userSessionService"
import FormatService from './FormatService'
import TimeUtil from "./timeUtil"

class ChannelPlan
{
    constructor()
    {
        if (!this.instance)
        {
            this.instance = this
            this.channelPlan = []
            this.resultTime = null
        }
        return this.instance
    }

    /**
     * Go get the channel plan from the server and the stats from the latest run and store it
     * @returns {Promise<T>}
     */
    initChannelPlan ()
    {
        let hasCustomer = !!UserSessionService.getCurrentCustomer().id
        let hasPermission = (UserSessionService.hasRadioPermission() && UserSessionService.hasFreqMapPermission())
        if (hasPermission && hasCustomer) {
            return axios.get("/api/frequencymappings/v1/balances").then(result => {
                    if (result.data) {
                        // Now that we have the latest channel plan, get the stats for the channels
                        return this.initChannelPlanStats(result.data.records).then(result => {
                            return this.channelPlan;
                        });
                    } else {
                        console.warn("No data returned from freqMap balances");
                        this.channelPlan = [];
                        return this.channelPlan;
                    }
                })
                .catch(error => {
                    console.error("initChannelPlan.get error:",error.response ? error.response : error);
                    return Promise.reject(error.response);
                });
        } else {
            console.warn("Radios Access Denied");
            this.channelPlan = [];
            return this.channelPlan;
        }
    }

    /**
     * Get the stats for the channel plan and create our channel plan object
     * @param channelPlan
     * @returns {Promise<T>}
     */
    initChannelPlanStats (channelPlan)
    {
        // First we have to get the latest run of the channel balancing job
        let sortByList = {sortByList: [{ fieldName: "channelBalanceResultCreateTime.displayString", direction: "Descending"}]}
        return axios.post('/api/radios/v1/channelresult/search', {page: 1, perPage: 1,...sortByList}).then (result => {
            if (result?.data?.records?.length > 0)
            {
                this.resultTime = TimeUtil.nmFormatDateDisplayStringWithTZ(result.data.records[0].channelBalanceResultCreateTime)
                // Now we want to get the list of channel stats results from that job
                let filterList = {filterList: [{
                    fieldName: "channelBalanceResultHistoryId",
                    fieldType: "VALUES",
                    valueOperator: "EQUALS",
                    values: [result.data.records[0].channelBalanceResultHistoryId]
                }]}
                return axios.post('/api/radios/v1/channelresult/search', {page: 1, perPage: 200,...filterList}).then (statsResult =>
                {
                    // Process the data and create a nice little channel plan object that the rest of the code can use
                    if (statsResult.data)
                    {
                        let statsAry = statsResult.data.records
                        channelPlan.forEach(channel => {
                            let channelStat = statsAry.find(stat => (stat.downlink?.frequency === channel.downlink?.frequency) || (stat.uplink?.frequency === channel.uplink?.frequency))
                            if (channelStat)
                            {
                                channel.allocation = channelStat.allocation
                                channel.counts = channelStat.counts
                            }
                            else // If not match, then init stats to 0
                            {
                                channel.allocation = {actual: 0, actualTunable: 0, goal: 0}
                                channel.counts = {towers: 0, towersPrimary: 0, towersSecondary: 0,
                                    radios: {added: 0, current: 0, nonTunable: 0, removed: 0, target: 0}
                                }
                            }
                            // We are going to add some gui generic rendering fields here as it seems like a good idea
                            // instead of having to convert these channel objects everytime we need to display them
                            // This might be a bad idea, but let's try it
                            channel.uplink.id = channel.uplink.frequency
                            channel.uplink.name = channel.uplink.frequencyName
                            if (channel.downlink)
                            {
                                channel.downlink.id = channel.downlink.frequency
                                channel.downlink.name = channel.downlink.frequencyName
                            }
                            this.channelPlan.push (channel)
                        })
                    }
                })
            }
            else
            {
                channelPlan.forEach(channel => {
                    channel.allocation = {actual: 0, actualTunable: 0, goal: 0}
                    channel.counts =
                    {
                        towers: 0, towersPrimary: 0, towersSecondary: 0,
                        radios: {added: 0, current: 0, nonTunable: 0, removed: 0, target: 0}
                    }
                    this.channelPlan.push (channel)
                })
            }
        }).catch(error => {
            console.error('initChannelPlanStats.get error:', error.response ? error.response : error)
            return Promise.reject(error.response)
        })
    }

    /**
     * Get the channel plan object for the given frequency looking in the direction asked or in both directions if not given
     * @param freq - the frequency to find
     * @param direction - the direction to look in, leave null to look in both directions, direction is from the Tower point of view
     * @returns {*|null}
     */
    getChannelPlanForFrequency (freq, direction)
    {
        for (const cpObj of this.channelPlan)
        {
            if (direction === 'TRANSMIT')
            {
                if (cpObj?.downlink?.frequency === freq)
                {
                    return (cpObj)
                }
            }
            else if (direction === 'RECEIVE')
            {
                if (cpObj?.uplink?.frequency === freq)
                {
                    return (cpObj)
                }
            }
            else
            {
                if ((cpObj?.downlink?.frequency === freq) || (cpObj?.uplink?.frequency === freq))
                {
                    return (cpObj)
                }
            }
        }
        return null
    }

    getTrafficMapFromChannelPlan ()
    {
        let trafficMap = {}

        this.channelPlan.forEach(channel => {
            if (trafficMap[channel.trafficType] == null)
            {
                if (channel.downlink)
                {
                    trafficMap[channel.trafficType] = {uplink: [], downlink:[]}
                }
                else
                {
                    trafficMap[channel.trafficType] = {uplink:[]}
                }
            }
            if (channel.downlink)
            {
                trafficMap[channel.trafficType].downlink.push (channel.downlink)
            }
            trafficMap[channel.trafficType].uplink.push (channel.uplink)
        })
        return (trafficMap)
    }

    getTrafficTypesForFrequency (freq, direction)
    {
        let trafficTypes = []
        for (const cpObj of this.channelPlan)
        {
            if (direction === 'TRANSMIT')
            {
                // Check to see if we match the frequency and the trafficType is not already in the list
                if ((cpObj.downlink?.frequency === freq) && (!trafficTypes.includes(cpObj.trafficType)))
                {
                    trafficTypes.push (cpObj.trafficType)
                }
            }
            else if (direction === 'RECEIVE')
            {
                if ((cpObj.uplink?.frequency === freq) && (!trafficTypes.includes(cpObj.trafficType)))
                {
                    trafficTypes.push (cpObj.trafficType)
                }
            }
            else
            {
                if (((cpObj.downlink?.frequency === freq) || (cpObj.uplink?.frequency === freq)) &&
                    (cpObj.uplink?.frequency === freq) && (!trafficTypes.includes(cpObj.trafficType)))
                {
                    trafficTypes.push(cpObj.trafficType)
                }
            }
        }
        return trafficTypes
    }

    /**
     * Based on the planning object for the frequency, create icons to tell the user information about the freq
     * @param cpObj - channel planning object for the frequency
     * @param freq - the frequency we want icons for
     * @returns {*[]}
     */
    getIconList (cpObj, freq)
    {
        if (!auth.isTuningFeatureEnabled())
        {
            return []
        }
        let iconAry = []
        // If there is no frequency, then don't show a bunch of icons with a blank freq, that would be weird
        if (freq == null)
        {
            return iconAry
        }
        let resultStr = this.resultTime ? " (" + this.resultTime + ")" : ".  No channel balancing results yet"
        if (cpObj === null)
        {
            iconAry.push ({iconName: "wifi_channel", iconColor: "error", title: "Not in channel plan" + resultStr})
        }
        else
        {
            if (cpObj.counts.tower === 0)
            {
                iconAry.push ({iconName: "cell_tower", iconColor: "warning", title: "No base stations on this frequency" + resultStr})
            }
            if (cpObj.counts.radios.current === 0)
            {
                iconAry.push ({iconName: "wifi_off", iconColor: "warning", title: "No radios on this frequency" + resultStr})
            }
        }
        return iconAry
    }
    mapChannelBalancingByTrafficType (channelBalanceObj)
    {
        let trafficMap = []
        channelBalanceObj.forEach (freq =>
        {
            if (trafficMap[freq.trafficType] === undefined)
            {
                trafficMap[freq.trafficType] = []
            }
            freq.uplink.displayName = FormatService.nmChannelFormatter(freq.uplink)
            if (freq.downlink)
            {
                freq.downlink.displayName = FormatService.nmChannelFormatter(freq.downlink)
            }
            trafficMap[freq.trafficType].push (freq)
        })
        return (trafficMap)
    }

    /**
     * Get the channel order for a given frequency and traffic type string
     * @param trafficString
     * @param freq
     * @returns {string|null|any|number}
     */
    getChannelOrderForFrequency (trafficString, freq)
    {
        if (trafficString == null)
        {
            return 999
        }
        let trafficType = trafficString
        let direction = 'uplink'
        // Adjust traffic type and direction for bulk traffic types
        if (trafficString.includes('BULK_UP'))
        {
            trafficType = "BULK"
        }
        else if (trafficString.includes('BULK_DOWN'))
        {
            trafficType = "BULK"
            direction = "downlink"
        }
        // Now find the channel plan object for this traffic type and direction
        for (const cpObj of this.channelPlan)
        {
            if (cpObj.trafficType === trafficType)
            {
                if (cpObj[direction]?.frequency.toString() === freq)
                {
                    return cpObj.channelOrder
                }
            }
        }
        return 999
    }
}
export default new ChannelPlan()
